import { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useInView } from 'react-intersection-observer'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Grid, { GridSize } from '@mui/material/Grid'
import { Button } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import FakeOffers, {
  useCat4561ContactFieldOrder
} from 'components/AbTestComponents/CAT4561'
import HotlineFallback from 'components/HotlineFallback'
import CategoryLayout from 'components/Layout/Category'
import LegalHint from 'components/LegalHint'
import SaveButton from 'components/SaveButton/SaveButton'
import SaveButtonDialog, {
  SaveButtonDialogState
} from 'components/SaveButton/SaveButtonDialog'
import { useErrorState } from 'context/Error/hook'
import useEmotionStyles from 'hooks/useEmotionStyles'
import { useSaveButton } from 'hooks/useSaveButton'
import { useDebouncedEffect } from 'hooks/useDebouncedEffect'
import { useStores } from 'stores/utils/hooks/useStores'
import LegalHintTexts from 'texts/common/copy/legal'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import ProgressiveDisclosure from 'components/ProgressiveDisclosure'
import { contactPageCAT4561VariantStyles } from './ContactPageCAT4561Variant.styles'

type Props = {
  onBack?: () => void
  onNextWithTracking: () => void
  sizeOfContinueButton: GridSize
  sizeOfBackButton: GridSize
  sizeOfSaveButton: GridSize
  isBackButtonHidden: boolean
  backButtonVariant: 'text' | 'outlined' | 'contained'
  saveButtonVariant: 'outlined' | 'contained'
  backLabelText: string | undefined
}

const ContactPageCAT4561Variant = observer(
  ({
    onBack,
    onNextWithTracking,
    sizeOfContinueButton,
    sizeOfBackButton,
    sizeOfSaveButton,
    isBackButtonHidden,
    backButtonVariant,
    saveButtonVariant,
    backLabelText
  }: Props) => {
    const {
      state: { message },
      hasCooldown
    } = useErrorState()
    const { ref, inView } = useInView()
    const {
      page: { isFirstDisplayedPage },
      navigation: { currentPageInSession },
      traversal: { data }
    } = useStores()
    const [saveButtonDialogState, setSaveButtonDialogState] =
      useState<SaveButtonDialogState>('none')
    const { isSaveButtonVisible, save, isSaveButtonEnabled } = useSaveButton({
      onSuccess: () => {
        setSaveButtonDialogState('success')
      },
      onError: () => {
        setSaveButtonDialogState('error')
      }
    })
    const { flexBasisGrid, buttonFullHeight } = useEmotionStyles(
      contactPageCAT4561VariantStyles
    )
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const isFirstPage = isFirstDisplayedPage(currentPageInSession)
    const { lastFieldValidator, lastFieldGateValue } =
      useCat4561ContactFieldOrder('debtors.primary')

    useDebouncedEffect(
      () => {
        if (inView) {
          trackInAmplitude(AmplitudeEvent.CtaIsVisible)
        }
      },
      300,
      [inView]
    )

    useEffect(() => {
      setSaveButtonDialogState('none')
    }, [data, currentPageInSession])

    return (
      <CategoryLayout ref={ref}>
        {message && <HotlineFallback message={message} />}
        <SaveButtonDialog state={saveButtonDialogState} />
        <Grid
          container
          spacing={1}
          direction={isMobile || isSaveButtonVisible ? 'column' : 'row-reverse'}
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            className={flexBasisGrid}
            container
            item
            spacing={1}
            direction={isMobile ? 'column' : 'row-reverse'}
            xs={12}
          >
            <Grid item xs={12} sm={sizeOfContinueButton}>
              <Button
                size="large"
                fullWidth
                id="next-submit"
                name="next-submit"
                color="primary"
                variant="contained"
                disabled={hasCooldown}
                onClick={onNextWithTracking}
                endIcon={<ArrowForwardIcon />}
                data-page={currentPageInSession}
                data-page-first={isFirstPage}
                sx={{ height: '48px', fontSize: 16 }}
              >
                Weiter
              </Button>
            </Grid>
            {isSaveButtonVisible && (
              <Grid item xs={12} sm={sizeOfSaveButton}>
                <div
                  className={buttonFullHeight}
                  data-testid="save-button-container-formNav"
                >
                  <SaveButton
                    isSaveButtonEnabled={isSaveButtonEnabled}
                    onClick={save}
                    variant={saveButtonVariant}
                  />
                </div>
              </Grid>
            )}
            {!isBackButtonHidden && (
              <Grid item xs={12} sm={sizeOfBackButton}>
                <Button
                  className={buttonFullHeight}
                  size="large"
                  type="button"
                  id="back-submit"
                  name="back-submit"
                  onClick={onBack}
                  color="primary"
                  variant={backButtonVariant}
                  startIcon={<ArrowBackIcon />}
                  fullWidth
                >
                  {backLabelText}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
        <ProgressiveDisclosure
          validator={(v) => !!lastFieldValidator(v)}
          value={lastFieldGateValue}
          disableLayout
          testId="legal-hint-contact"
        >
          <LegalHint legalText={LegalHintTexts.defaultLegalHint} />
        </ProgressiveDisclosure>
        <FakeOffers
          fakeBankListNames={['postbank', 'ingDiba', 'commerzbank']}
        />
      </CategoryLayout>
    )
  }
)

export default ContactPageCAT4561Variant
